import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AnnouncementMedia } from "@/components/AnnouncementMedia";
import { LockKey, Megaphone, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const PRIORITY = {
    urgent: { label: "Urgent", cls: "bg-[#E6002C] text-white border border-[#E6002C]" },
    important: { label: "Important", cls: "bg-amber-100 text-amber-900 border border-amber-200" },
    normal: { label: "Notice", cls: "bg-slate-100 text-slate-700 border border-slate-200" },
};

export const PriorityBadge = ({ priority }) => {
    const p = PRIORITY[priority] || PRIORITY.normal;
    return <Badge className={p.cls} data-testid="announcement-priority-badge">{p.label}</Badge>;
};

export const formatStamp = (iso) => (iso ? new Date(iso).toLocaleString() : null);

/** 4-digit PIN gate. The content is not in the payload until this succeeds. */
export const AnnouncementPinGate = ({ announcement, onUnlocked }) => {
    const [pin, setPin] = useState("");
    const [busy, setBusy] = useState(false);
    const [noPin, setNoPin] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (pin.length !== 4) return toast.error("Enter your 4-digit PIN.");
        setBusy(true);
        try {
            const { data } = await api.post(`/announcements/${announcement.id}/unlock`, { pin });
            setPin("");
            onUnlocked(data);
        } catch (err) {
            const detail = formatApiErrorDetail(err.response?.data?.detail) || "Could not unlock";
            if (detail.toLowerCase().includes("my profile")) setNoPin(true);
            toast.error(detail);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="rounded-md border border-[#E2E8F0] bg-[#F8FAFC] p-5" data-testid="announcement-pin-gate">
            <div className="flex items-start gap-2.5">
                <LockKey size={20} weight="duotone" className="text-[#0066D9] mt-0.5" />
                <div>
                    <div className="text-sm font-semibold text-[#1F2A44]">This notice is private</div>
                    <p className="text-xs text-slate-500 mt-1">
                        Enter your personal 4-digit Announcement PIN to open it. Nobody else can read it for you.
                    </p>
                </div>
            </div>
            {noPin ? (
                <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
                    data-testid="announcement-no-pin-warning">
                    <Warning size={14} className="inline mr-1 -mt-0.5" />
                    You haven't set an Announcement PIN yet. Go to{" "}
                    <a href="/profile" className="underline font-semibold" data-testid="announcement-set-pin-link">
                        My Profile
                    </a>{" "}
                    to create one, then come back to open this notice.
                </div>
            ) : (
                <form onSubmit={submit} className="mt-4 flex flex-wrap items-end gap-3">
                    <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                            Your PIN
                        </Label>
                        <Input
                            type="password" inputMode="numeric" pattern="[0-9]*" maxLength={4}
                            autoComplete="one-time-code" value={pin}
                            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                            className="h-11 w-32 border-[#E2E8F0] tracking-[0.5em] text-center bg-white"
                            data-testid="announcement-unlock-pin-input"
                        />
                    </div>
                    <Button type="submit" disabled={busy} className="bg-[#003366] hover:bg-[#0066D9] h-11"
                        data-testid="announcement-unlock-button">
                        {busy ? "Opening…" : "Unlock notice"}
                    </Button>
                </form>
            )}
        </div>
    );
};

/** Title + message once readable, or the PIN gate while locked. */
export const AnnouncementBody = ({ announcement, onUnlocked }) => {
    if (announcement.locked) {
        return <AnnouncementPinGate announcement={announcement} onUnlocked={onUnlocked} />;
    }
    return (
        <div data-testid="announcement-body">
            <div className="flex items-start gap-2.5">
                <Megaphone size={20} weight="duotone" className="text-[#0066D9] mt-0.5 shrink-0" />
                <h3 className="text-base md:text-lg font-semibold text-[#1F2A44] break-words"
                    style={{ fontFamily: "Outfit" }} data-testid="announcement-title">
                    {announcement.title}
                </h3>
            </div>
            {announcement.message ? (
                <p className="mt-3 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap break-words"
                    data-testid="announcement-message">
                    {announcement.message}
                </p>
            ) : null}
            <AnnouncementMedia announcementId={announcement.id} media={announcement.media} />
            <div className="mt-4 text-xs text-slate-500">
                {announcement.published_by ? <>Published by {announcement.published_by} · </> : null}
                Expires {formatStamp(announcement.expires_at)}
            </div>
        </div>
    );
};
